import {
    createContext,
    FC,
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from 'react'
import vwoSDK, { vwoInstance } from 'vwo-node-sdk'

import SuspenseLoader from '~/core/SuspenseLoader'
import { getenv } from '~/settings'
import logger from '~/lib/logger'
import { vwoUserStorage } from '~/lib/providers/Vwo/vwoUserStorage'

const vwoContext = createContext<{ vwoClient: vwoInstance | null }>({
    vwoClient: null,
})

export const VwoProvider: FC<PropsWithChildren> = ({ children }) => {
    const [loading, setLoading] = useState(true)
    const [value, setValue] = useState<{ vwoClient: vwoInstance | null }>({
        vwoClient: null,
    })

    useEffect(() => {
        const vwoAccountId = getenv('VWO_ACCOUNT_ID')
        const vwoSdkKey = getenv('VWO_SDK_KEY')

        ;(async () => {
            if (!vwoAccountId || !vwoSdkKey) {
                logger.error(
                    "Couldn't get vwo account id or sdk key from config.",
                )
            } else {
                try {
                    const settingsFile = await vwoSDK.getSettingsFile(
                        vwoAccountId,
                        vwoSdkKey,
                    )

                    setValue({
                        vwoClient: vwoSDK.launch({
                            settingsFile,
                            userStorageService: vwoUserStorage,
                            // uncomment it if you need to debug vwo workflow
                            // logging: {
                            //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            //     // @ts-ignore
                            //     level: vwoSDK.LogLevelEnum.DEBUG,
                            // },
                        }),
                    })
                } catch (e) {
                    logger.error("Couldn't load VWO client", e)
                }
            }

            setLoading(false)
        })()
    }, [])

    if (loading) {
        return <SuspenseLoader />
    }

    return <vwoContext.Provider value={value}>{children}</vwoContext.Provider>
}

export const useVwoContext = () => {
    const context = useContext(vwoContext)

    if (!context) {
        throw new Error('useVwoContext must be used within a VwoProvider')
    }

    return context
}
